import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Button,
  Grid2,
  Skeleton,
  Chip,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { useRemoteGetMutateAllCostCentersForCompany } from "../../../hooks/Remote/Companies/useRemoteGetMutateAllCostCentersForCompany";
import { InfoIconWithTooltip } from "../../../components/Title/InfoIcon";
import DepartmentCard from "./department-card.component";
import AddDepartmentModal from "./add-department-modal.partial";
import { DEPARTMENT_CARD_WIDTH_PX } from "../admin-styled.component";

export type Department = {
  id?: string;
  companyId: number;
  name: string;
  costCenterResponsibleMail?: string;
  active?: boolean;
  isDepartment?: boolean;
  userInfo?: {
    fullName: string;
    photo?: string | null;
  };
  description?: string;
  color?: string | null;
  aliases?: string[];
};

const Departments = () => {
  const {
    userInformation: { companyId }
  } = useSelector((state: RootState) => state.login);

  const { t } = useTranslation();

  const [addDepartment, setAddDepartment] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<Department[]>();

  const { mutateAsync: getDepartments, isLoading: isRemoteDepartmentsLoading } =
    useRemoteGetMutateAllCostCentersForCompany(companyId);

  /**
   * Fetches all departments using GET mutation and updates the departments state variable.
   */
  const fetchAllCostCenterForCompany = () =>
    getDepartments()
      .then(data => setDepartments(data))
      .catch(() => setDepartments([]));

  const filteredDepartments = useMemo(() => {
    return departments.filter(department => {
      if (selectedDepartments?.length) {
        return selectedDepartments?.some(selectedDepartment => {
          return selectedDepartment.id === department.id;
        });
      }
      return true;
    });
  }, [departments, selectedDepartments]);

  useEffect(() => {
    // initially fetch department data
    fetchAllCostCenterForCompany();
  }, []);

  return (
    <>
      <Grid2
        sx={{ marginBottom: "20px", gap: "10px" }}
        container
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center"
          }}
        >
          <InfoIconWithTooltip tooltipText={t("_departmentInfo")} />
          <Button
            onClick={() => setAddDepartment(true)}
            style={{
              marginLeft: 10
            }}
            color={"primary"}
            data-testid="add-departments-btn"
          >
            {t("Add department")}
          </Button>
        </div>

        <Autocomplete
          multiple
          disableCloseOnSelect
          style={{ width: 300 }}
          limitTags={1}
          data-testid={"department-find-autocomplete"}
          id="tags-outlined7"
          options={departments ?? []}
          noOptionsText={t("No Options")}
          filterSelectedOptions
          getOptionLabel={option => option.name}
          onChange={(event, values) => {
            event.persist();
            setSelectedDepartments(values);
          }}
          value={selectedDepartments || []}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              size={"small"}
              fullWidth
              placeholder={t("Find department")}
            />
          )}
          renderTags={(tagValue, getTagProps) => {
            return tagValue.map((option, index) => (
              <Chip {...getTagProps({ index })} label={option.name} key={option.id} />
            ));
          }}
        />
      </Grid2>

      {/* is loading */}
      <Grid2 container direction="row" justifyContent="flex-start" alignItems="flex-start">
        {isRemoteDepartmentsLoading && (
          <Skeleton
            variant={"rectangular"}
            sx={{ height: 236, width: DEPARTMENT_CARD_WIDTH_PX, p: "20px", mr: 1 }}
          />
        )}

        {/* when no departments exist */}
        {!isRemoteDepartmentsLoading && !departments.length && (
          <Skeleton
            variant={"rectangular"}
            sx={{ height: 240, width: DEPARTMENT_CARD_WIDTH_PX, p: "20px", mr: 1 }}
            animation={false}
          >
            <Box
              sx={{
                textAlign: "center",
                alignContent: "center",
                height: 236,
                width: DEPARTMENT_CARD_WIDTH_PX
              }}
            >
              <Typography variant={"body2"} sx={{ visibility: "visible" }}>
                {t("No departments found")}
              </Typography>
            </Box>
          </Skeleton>
        )}

        {filteredDepartments.map(department => (
          <DepartmentCard
            key={department.id}
            departmentName={department.name}
            description={department.description}
            active={department.active}
            userInfo={department.userInfo}
            departments={departments}
            setDepartments={setDepartments}
            costCenterResponsibleMail={department.costCenterResponsibleMail || ""}
            companyId={department.companyId}
            isDepartment={department.isDepartment || false}
            id={department.id || ""}
            color={department.color}
            aliases={department.aliases}
            fetchAllCostCenterForCompany={fetchAllCostCenterForCompany}
          />
        ))}
      </Grid2>

      <AddDepartmentModal
        open={addDepartment}
        setOpen={setAddDepartment}
        companyId={companyId}
        active={false}
        isDepartment={true}
        departmentName={""}
        departments={departments}
        setDepartments={setDepartments}
        id={""}
        description={""}
        userInfo={{ fullName: "", photo: null }}
        costCenterResponsibleMail={""}
        aliases={[]}
        fetchAllCostCenterForCompany={fetchAllCostCenterForCompany}
      />
    </>
  );
};
export default Departments;
